import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { CalendarClock } from 'lucide-react';

import { Button } from '@/components/Button/Button';
import { MarkdownRenderer } from '@/features/chat/MarkdownRenderer';

import {
  coachVisibleMaxScore,
  isThreadClosed,
  isThreadOpen,
  normalizeCoachSection,
  threadAcceptsMessages,
  type CoachThread,
} from './kaiCoachContent';
import type { RawKaiCoachSection } from './kaiCoachTypes';
import { useKaiCoachConversation } from './useKaiCoachConversation';
import { usePrepareKaiCoachSession } from './usePrepareKaiCoachSession';
import styles from './KaiCoachStudentView.module.css';

interface KaiCoachStudentViewProps {
  courseId: string;
  // The student's earned points per scorable-unit uid, read from their loaded
  // course component's section_stats. Absent in the instructor preview.
  earnedByUnit?: Record<string, number>;
  // The thread to open with (its key: the session uid, or "session/activity"),
  // e.g. from a Due Soon link. Used only if the viewer may select it.
  initialThreadKey?: string | null;
  moduleId: string;
  section: RawKaiCoachSection;
  // Injectable for tests/SSR determinism; defaults to the current time.
  now?: number;
  // INSTRUCTOR-ONLY display reveal: lets a not-yet-open session be selected and
  // previewed. It is display-only by construction — the participation score, the
  // earned total, and whether a conversation is loaded all still use the real
  // open date, so a revealed session cannot change scoring or create a thread.
  // Never true for a real student: the only callers derive it from canEdit (an
  // instructor outside student view) or from an active student-view session,
  // which requires a masked staff test account.
  revealLockedThreads?: boolean;
}

// Date AND time: sessions often open (and close) on the same day at different
// times, so a date alone made them indistinguishable in the list.
function formatDateTime(millis: number): string {
  return new Date(millis).toLocaleString(undefined, {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// Trim trailing-zero decimals so "1" and "1.5" both read cleanly.
function formatPoints(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(2)));
}

// The assignment card: the thread's prompt and, on a goal-reached coach, the
// A date as a <time>: readable text, with the machine-readable instant.
function DateTime({ millis }: { millis: number }) {
  return <time dateTime={new Date(millis).toISOString()}>{formatDateTime(millis)}</time>;
}

// The selected thread's deadline, where the student writes: "Due" while it is
// open, "Closed" once it has passed. Nothing when the thread has no due date.
function DueLine({ closed, dueAtMillis }: { closed: boolean; dueAtMillis: number | null }) {
  if (dueAtMillis === null) {
    return null;
  }
  return (
    <p className={styles.dueLine} data-closed={closed ? 'true' : 'false'}>
      <CalendarClock aria-hidden="true" className={styles.dueLineIcon} size={16} />
      <span>
        <span className={styles.dueLineLabel}>{closed ? 'Closed' : 'Due'}</span>{' '}
        <DateTime millis={dueAtMillis} />
      </span>
    </p>
  );
}

// goal the instructor told students about. Either alone still makes a card.
function PromptCard({ goalSummary, promptMarkdown }: { goalSummary: string; promptMarkdown: string }) {
  if (!promptMarkdown && !goalSummary) {
    return null;
  }
  return (
    <div className={styles.promptCard} role="note">
      <p className={styles.promptHeading}>Assignment</p>
      {promptMarkdown ? <MarkdownRenderer content={promptMarkdown} /> : null}
      {goalSummary ? (
        <p className={styles.goalLine}>
          <span className={styles.goalLabel}>Goal:</span> {goalSummary}
        </p>
      ) : null}
    </div>
  );
}

export function KaiCoachStudentView({
  courseId,
  earnedByUnit,
  initialThreadKey = null,
  moduleId,
  now = Date.now(),
  revealLockedThreads = false,
  section,
}: KaiCoachStudentViewProps) {
  const normalized = useMemo(() => normalizeCoachSection(section), [section]);
  const earned = earnedByUnit ?? {};

  // Score summary: only when credit is on and something is open to earn.
  const showScores = normalized.creditEnabled;
  // Goal-reached credit: the unit's goal line and hint replace the message count.
  const goalMethod = showScores && normalized.creditMethod === 'goalReached';
  const maxScore = useMemo(
    () => coachVisibleMaxScore(normalized.threads, now),
    [normalized.threads, now],
  );
  const totalEarned = useMemo(
    () =>
      normalized.threads.reduce(
        (sum, thread) =>
          isThreadOpen(thread, now) ? sum + (earned[thread.scoreUnitUid] ?? 0) : sum,
        0,
      ),
    [earned, normalized.threads, now],
  );

  // A coach with no sessions is a single always-on thread.
  const threads = useMemo<CoachThread[]>(() => {
    if (normalized.threads.length > 0) {
      return normalized.threads;
    }
    return [
      {
        activityUid: '',
        dueAtMillis: null,
        focusAreas: [],
        goalSummary: '',
        hasGoal: false,
        key: 'default',
        label: 'Coaching conversation',
        openAtMillis: null,
        points: 0,
        promptMarkdown: '',
        requiredInteractions: 0,
        scoreUnitUid: '',
        sessionLabel: '',
        sessionUid: '',
      },
    ];
  }, [normalized.threads]);

  // Land on the linked thread when the viewer may select it; otherwise on a
  // thread the student can still write in; failing that, one they can at least
  // read; failing that, the first.
  const firstOpenKey = useMemo(() => {
    const linked = initialThreadKey
      ? threads.find((thread) => thread.key === initialThreadKey)
      : undefined;
    if (linked && (revealLockedThreads || isThreadOpen(linked, now))) {
      return linked.key;
    }
    const writable = threads.find((thread) => threadAcceptsMessages(thread, now));
    const open = threads.find((thread) => isThreadOpen(thread, now));
    return (writable ?? open ?? threads[0])?.key ?? null;
  }, [initialThreadKey, now, revealLockedThreads, threads]);

  const [selectedKey, setSelectedKey] = useState<string | null>(firstOpenKey);
  const activeThread =
    threads.find((thread) => thread.key === selectedKey) ?? null;
  const activeOpen = activeThread ? isThreadOpen(activeThread, now) : false;

  const conversation = useKaiCoachConversation({
    activityUid: activeThread?.activityUid ?? '',
    courseId,
    enabled: Boolean(activeThread) && activeOpen,
    moduleId,
    // When credit is on, a sent message may change the participation score; ask
    // the hook to refresh the loaded component so the score display updates live.
    refreshComponentOnSend: showScores,
    sessionUid: activeThread?.sessionUid ?? '',
  });

  // Closed by its due date, or by the server refusing a message after this
  // tab's clock still said open. Either way the transcript stays readable and
  // the composer goes. Independent of the reveal flag: revealing hidden content
  // never reopens a closed thread.
  const activeClosed =
    activeThread !== null &&
    (isThreadClosed(activeThread, now) || conversation.isClosedByServer);

  // Goal-reached credit: once the student's work reaches the goal the thread is
  // complete. Like a closed thread it stays readable and takes no more
  // messages; the server refuses them too.
  const activeComplete = goalMethod && activeThread !== null && conversation.isGoalReached;

  // When the composer goes while the student is in it — Kai's reply completed
  // the thread, or the server refused a send as closed — focus would drop to
  // the page. Move it to the note that replaced the composer. Only a change in
  // the same thread counts: opening a thread that is already complete or
  // closed leaves focus where the student put it.
  const composerGone = activeComplete || activeClosed;
  const lockedNoteRef = useRef<HTMLParagraphElement>(null);
  const lastLockRef = useRef<{ gone: boolean; key: string | null }>({ gone: false, key: null });
  const activeKey = activeThread?.key ?? null;
  useEffect(() => {
    const last = lastLockRef.current;
    lastLockRef.current = { gone: composerGone, key: activeKey };
    const focusLost = document.activeElement === null || document.activeElement === document.body;
    if (last.key === activeKey && !last.gone && composerGone && focusLost) {
      lockedNoteRef.current?.focus();
    }
  }, [activeKey, composerGone]);

  // Off the critical path: warm the session's shared context root so the first
  // message in any of its activities can branch from it instead of uploading
  // the chapter material itself. Nothing here is awaited or displayed. A closed
  // thread gets no root: there is no first message to prepare for.
  usePrepareKaiCoachSession({
    activityUid: activeThread?.activityUid ?? '',
    courseId,
    enabled:
      activeThread !== null && threadAcceptsMessages(activeThread, now) && !activeComplete,
    moduleId,
    sessionUid: activeThread?.sessionUid ?? '',
  });

  const [draft, setDraft] = useState('');
  // The focus area the student picks for their NEXT message ('' = Other, the
  // default). Reset whenever the active thread changes so a prior thread's
  // choice never carries over.
  const [selectedFocusAreaUid, setSelectedFocusAreaUid] = useState('');
  useEffect(() => {
    setSelectedFocusAreaUid('');
  }, [selectedKey]);
  const focusAreas = activeThread?.focusAreas ?? [];

  const contextLabel = useMemo(() => {
    if (!activeThread) {
      return '';
    }
    return activeThread.sessionLabel &&
      activeThread.sessionLabel !== activeThread.label
      ? `${activeThread.sessionLabel} · ${activeThread.label}`
      : activeThread.label;
  }, [activeThread]);

  const closedMessage = useMemo(() => {
    if (!activeThread) {
      return '';
    }
    const when =
      activeThread.dueAtMillis !== null && isThreadClosed(activeThread, now)
        ? ` on ${formatDateTime(activeThread.dueAtMillis)}`
        : '';
    return revealLockedThreads
      ? `Preview — closed to students${when}. They can still read the conversation but can no longer send messages.`
      : `This ${normalized.sessionDimensionLabel.toLowerCase()} closed${when}. You can still read the conversation, but new messages can’t be sent.`;
  }, [activeThread, normalized.sessionDimensionLabel, now, revealLockedThreads]);

  // An activity of a split session has its own goal, so its complete note names
  // the activity rather than the whole session.
  const completeMessage = revealLockedThreads
    ? 'Preview — goal reached. Once students reach the goal they can read the conversation but can no longer send messages.'
    : `You reached this ${(activeThread?.activityUid
        ? normalized.activityDimensionLabel
        : normalized.sessionDimensionLabel
      ).toLowerCase()}’s goal, so this conversation is complete. You can still read it, but new messages can’t be sent.`;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!draft.trim() || conversation.isSending) {
      return;
    }
    void conversation.sendMessage(draft, selectedFocusAreaUid);
    setDraft('');
  }

  return (
    <section className={styles.page}>
      {normalized.orientationMarkdown ? (
        <div className={styles.orientation}>
          <MarkdownRenderer content={normalized.orientationMarkdown} />
        </div>
      ) : null}

      {showScores && maxScore > 0 ? (
        <div className={styles.scoreBanner}>
          <span className={styles.scoreBannerLabel}>Coach score</span>
          <span className={styles.scoreBannerValue}>
            {formatPoints(totalEarned)} / {formatPoints(maxScore)}
          </span>
        </div>
      ) : null}

      <div className={styles.layout}>
        <nav aria-label={`${normalized.sessionDimensionLabel} list`} className={styles.nav}>
          <p className={styles.navHeading}>{normalized.sessionDimensionLabel}s</p>
          <ul className={styles.threadList}>
            {threads.map((thread) => {
              const open = isThreadOpen(thread, now);
              const isActive = thread.key === selectedKey;
              return (
                <li key={thread.key}>
                  <button
                    aria-current={isActive ? 'true' : undefined}
                    className={styles.threadButton}
                    data-active={isActive ? 'true' : 'false'}
                    disabled={!open && !revealLockedThreads}
                    onClick={() => setSelectedKey(thread.key)}
                    type="button"
                  >
                    <span className={styles.threadLabel}>
                      {thread.sessionLabel && thread.sessionLabel !== thread.label
                        ? `${thread.sessionLabel} · ${thread.label}`
                        : thread.label}
                    </span>
                    {!open && thread.openAtMillis !== null ? (
                      <span className={styles.lockNote}>
                        Opens <DateTime millis={thread.openAtMillis} />
                      </span>
                    ) : null}
                    {/* Every due date shows, even before the session opens, so
                        students can see what is coming. */}
                    {thread.dueAtMillis !== null ? (
                      <span
                        className={styles.dueNote}
                        data-closed={isThreadClosed(thread, now) ? 'true' : 'false'}
                      >
                        {isThreadClosed(thread, now) ? 'Closed' : 'Due'}{' '}
                        <DateTime millis={thread.dueAtMillis} />
                      </span>
                    ) : null}
                    {showScores && open && thread.points > 0 ? (
                      <span
                        className={styles.threadScore}
                        data-earned={
                          (earned[thread.scoreUnitUid] ?? 0) >= thread.points
                            ? 'true'
                            : 'false'
                        }
                      >
                        {formatPoints(earned[thread.scoreUnitUid] ?? 0)} /{' '}
                        {formatPoints(thread.points)} pt
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className={styles.conversation}>
          {!activeThread ? (
            <p className={styles.empty}>Select a {normalized.sessionDimensionLabel.toLowerCase()} to begin.</p>
          ) : !activeOpen && !revealLockedThreads ? (
            <p className={styles.empty}>
              This {normalized.sessionDimensionLabel.toLowerCase()}
              {activeThread.openAtMillis !== null
                ? ` opens ${formatDateTime(activeThread.openAtMillis)}.`
                : ' is not open yet.'}
              {activeThread.dueAtMillis !== null
                ? ` It’s due ${formatDateTime(activeThread.dueAtMillis)}.`
                : ''}
            </p>
          ) : !activeOpen ? (
            // Instructor preview of a session students cannot reach yet. The
            // authored content is shown, but there is no transcript or composer:
            // the conversation query stays disabled so no thread is created.
            // Mirrors the real student layout — context chip, assignment, the
            // message area that absorbs the slack, then the composer pinned to
            // the bottom — so the preview shows what a student will actually
            // see. The composer is inert: no thread is ever created here.
            <>
              <p className={styles.lockedPreviewNote} role="status">
                Preview
                {activeThread.openAtMillis !== null
                  ? ` — opens to students ${formatDateTime(activeThread.openAtMillis)}`
                  : ' — not open to students yet'}
                .
              </p>

              {contextLabel ? (
                <p className={styles.contextChip}>{contextLabel}</p>
              ) : null}

              <DueLine
                closed={isThreadClosed(activeThread, now)}
                dueAtMillis={activeThread.dueAtMillis}
              />

              <PromptCard
                goalSummary={goalMethod ? activeThread.goalSummary : ''}
                promptMarkdown={activeThread.promptMarkdown}
              />

              {goalMethod && activeThread.points > 0 && activeThread.hasGoal ? (
                <p className={styles.progressHint}>
                  {`Kai awards this ${normalized.sessionDimensionLabel.toLowerCase()}’s points when a student’s work meets the goal.`}
                </p>
              ) : showScores && !goalMethod && activeThread.requiredInteractions > 0 ? (
                <p className={styles.progressHint}>
                  {`Students send ${activeThread.requiredInteractions} message${
                    activeThread.requiredInteractions === 1 ? '' : 's'
                  } to earn this ${normalized.sessionDimensionLabel.toLowerCase()}’s points.`}
                </p>
              ) : null}

              <div className={styles.messages} />

              <form
                className={styles.composer}
                onSubmit={(event) => event.preventDefault()}
              >
                <label className={styles.srOnly} htmlFor="kai-coach-preview-composer">
                  Message Kai
                </label>
                <textarea
                  className={styles.input}
                  disabled
                  id="kai-coach-preview-composer"
                  placeholder="Students will message Kai here once this opens."
                  rows={2}
                />
                <Button disabled type="button" variant="primary">
                  Send
                </Button>
              </form>
            </>
          ) : (
            <>
              {contextLabel ? (
                <p className={styles.contextChip}>{contextLabel}</p>
              ) : null}

              <DueLine
                closed={isThreadClosed(activeThread, now)}
                dueAtMillis={activeThread.dueAtMillis}
              />

              <PromptCard
                goalSummary={goalMethod ? activeThread.goalSummary : ''}
                promptMarkdown={activeThread.promptMarkdown}
              />

              {goalMethod && activeThread.points > 0 && activeThread.hasGoal ? (
                <p className={styles.progressHint}>
                  {(earned[activeThread.scoreUnitUid] ?? 0) >= activeThread.points ||
                  conversation.isGoalReached
                    ? 'Goal reached.'
                    : `Kai awards this ${normalized.sessionDimensionLabel.toLowerCase()}’s points when your work meets the goal.`}
                </p>
              ) : showScores && !goalMethod && activeThread.requiredInteractions > 0 ? (
                (() => {
                  const sent = conversation.messages.filter(
                    (message) => message.role === 'user',
                  ).length;
                  const need = activeThread.requiredInteractions;
                  return (
                    <p className={styles.progressHint}>
                      {sent >= need
                        ? `Participation complete — you earned ${formatPoints(
                            activeThread.points,
                          )} point${activeThread.points === 1 ? '' : 's'}.`
                        : `${sent} of ${need} messages sent to earn this ${normalized.sessionDimensionLabel.toLowerCase()}’s points.`}
                    </p>
                  );
                })()
              ) : null}

              <div aria-live="polite" className={styles.messages}>
                {conversation.messages.map((message) => (
                  <div
                    className={styles.message}
                    data-role={message.role}
                    key={message.id}
                  >
                    {message.blocks.map((block, index) => {
                      if (block.type === 'goalReached') {
                        // Kudu's mark on the reply that earned the credit. It
                        // names no points: the score does.
                        return (
                          <p className={styles.goalBadge} key={index}>
                            <span aria-hidden="true" className={styles.goalBadgeMark}>
                              ✓{' '}
                            </span>
                            Goal reached
                          </p>
                        );
                      }
                      return block.type === 'markdown' && block.markdown ? (
                        <MarkdownRenderer content={block.markdown} key={index} />
                      ) : null;
                    })}
                  </div>
                ))}
                {conversation.isSending ? (
                  <p className={styles.thinking}>Kai is thinking…</p>
                ) : null}
              </div>

              {conversation.error ? (
                <p className={styles.error} role="alert">
                  {conversation.error}
                </p>
              ) : null}

              {activeComplete ? (
                <p className={styles.closedNote} ref={lockedNoteRef} role="status" tabIndex={-1}>
                  {completeMessage}
                </p>
              ) : activeClosed ? (
                <p className={styles.closedNote} ref={lockedNoteRef} role="status" tabIndex={-1}>
                  {closedMessage}
                </p>
              ) : (
                <>
                  {focusAreas.length > 0 ? (
                    <label
                      className={styles.focusPicker}
                      htmlFor="kai-coach-focus"
                      title="Pick what you want to work on in your next message. Choose Other to just chat with Kai."
                    >
                      <span className={styles.focusLabel}>
                        What do you want to focus on next?
                      </span>
                      <select
                        className={styles.focusSelect}
                        id="kai-coach-focus"
                        onChange={(event) =>
                          setSelectedFocusAreaUid(event.target.value)
                        }
                        value={selectedFocusAreaUid}
                      >
                        {focusAreas.map((area) => (
                          <option key={area.uid} value={area.uid}>
                            {area.title}
                          </option>
                        ))}
                        <option value="">Other</option>
                      </select>
                    </label>
                  ) : null}

                  <form className={styles.composer} onSubmit={handleSubmit}>
                    <label className={styles.srOnly} htmlFor="kai-coach-composer">
                      Message Kai
                    </label>
                    <textarea
                      className={styles.input}
                      id="kai-coach-composer"
                      onChange={(event) => setDraft(event.target.value)}
                      placeholder="Chat with Kai by typing here"
                      rows={2}
                      value={draft}
                    />
                    <Button
                      disabled={!draft.trim() || conversation.isSending}
                      type="submit"
                      variant="primary"
                    >
                      Send
                    </Button>
                  </form>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
